import React, { useState } from 'react';
import styles from './DownloadButton.module.css';
import axios from 'axios';
import keycloak from '../../keycloak'; // your Keycloak instance

const DownloadButton = ({ label, viewBy, activeTab, useActiveTab = false, buttonText, reportColumns, globalFilter }) => {
    const NO_RECORDS_MESSAGE = 'There are no records to download with your current filters.';
    const [showPdfOptions, setShowPdfOptions] = useState(false);

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    let startDate, endDate;

    // Format date as YYYY-MM-DD
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Determine start and end dates based on viewBy
    if (viewBy === 'currentYear') {
        startDate = `${currentYear}-01-01`;
        endDate = `${currentYear}-12-31`;
    } else if (viewBy === 'currentMonth') {
        const firstDayMonth = new Date(currentYear, currentMonth, 1);
        const lastDayMonth = new Date(currentYear, currentMonth + 1, 0);
        startDate = formatDate(firstDayMonth);
        endDate = formatDate(lastDayMonth);
    } else if (viewBy === 'currentPeriod') {
        let startPeriod, endPeriod;
        if (currentDay >= 15) {
            startPeriod = new Date(currentYear, currentMonth, 15);
            endPeriod = new Date(currentYear, currentMonth + 1, 15);
        } else {
            startPeriod = new Date(currentYear, currentMonth - 1, 15);
            endPeriod = new Date(currentYear, currentMonth, 15);
        }
        startDate = formatDate(startPeriod);
        endDate = formatDate(endPeriod);
    }

    const buildQueryURL = (includeAttachments = false, checkOnly = false) => {
        const base = process.env.NODE_ENV === 'production' ? '/api/rest' : 'http://localhost:8000/rest';
        let queryURL = `${base}/download/report?format=${label.toLowerCase()}`;

        if (label.toLowerCase() === 'pdf') {
            queryURL += `&includeAttachments=${includeAttachments}`;
        }
        if (viewBy !== 'all') {
            queryURL += `&startDate=${startDate}&endDate=${endDate}`;
        }
        if (useActiveTab && activeTab) {
            queryURL += `&activeTab=${encodeURIComponent(activeTab)}`;
        }
        if (label.toLowerCase() === 'csv' && Array.isArray(reportColumns) && reportColumns.length > 0) {
            queryURL += `&csvColumns=${encodeURIComponent(JSON.stringify(reportColumns))}`;
        }
        if (typeof globalFilter === 'string' && globalFilter.trim()) {
            queryURL += `&globalFilter=${encodeURIComponent(globalFilter.trim())}`;
        }
        if (checkOnly) {
            queryURL += '&checkOnly=true';
        }
        return queryURL;
    };

    const handleDownloadError = async (error) => {
        console.error(error);
        const status = error?.response?.status;
        const responseData = error?.response?.data;
        let serverMessage = '';

        if (typeof responseData === 'string') {
            serverMessage = responseData;
        } else if (responseData && typeof responseData.text === 'function') {
            try {
                serverMessage = await responseData.text();
            } catch (parseError) {
                console.error('Unable to parse download error response:', parseError);
            }
        }

        const normalizedServerMessage = (serverMessage || '').replace(/\.$/, '').trim();
        const normalizedExpectedMessage = NO_RECORDS_MESSAGE.replace(/\.$/, '').trim();
        if (status === 404 && normalizedServerMessage === normalizedExpectedMessage) {
            alert(NO_RECORDS_MESSAGE);
            return;
        }
        alert('Sorry, an error has occurred. Please try again.');
    };

    const runDownload = async (includeAttachments = false) => {
        try {
            console.log(`${label} Downloading...`);

            await keycloak.updateToken(5); // refresh if expires in 5 sec
            const token = keycloak.token;

            const response = await axios.get(buildQueryURL(includeAttachments, false), {
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `report.${label.toLowerCase()}`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            alert(`${label} Report successfully generated.`);
        } catch (error) {
            await handleDownloadError(error);
        }
    };

    const handleDownload = async () => {
        if (label.toLowerCase() === 'pdf') {
            try {
                await keycloak.updateToken(5);
                const token = keycloak.token;
                await axios.get(buildQueryURL(false, true), {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setShowPdfOptions(true);
                return;
            } catch (error) {
                await handleDownloadError(error);
                return;
            }
        }
        await runDownload(false);
    };

    return (
        <div className={styles.buttonContainer}>
            <button
                onClick={handleDownload}
                className={styles.downloadButton}
            >
                {buttonText || `Download as ${label}`}
            </button>
            {showPdfOptions && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalCard}>
                        <div className={styles.modalTitle}>Include invoice attachments in the downloaded PDF?</div>
                        <div className={styles.modalActions}>
                            <button
                                className={styles.modalYesButton}
                                onClick={() => {
                                    setShowPdfOptions(false);
                                    runDownload(true);
                                }}
                            >
                                Yes
                            </button>
                            <button
                                className={styles.modalNoButton}
                                onClick={() => {
                                    setShowPdfOptions(false);
                                    runDownload(false);
                                }}
                            >
                                No
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DownloadButton;
